// CORE-847 §3 — the idle life around Agapella's avatar: a warm glow that
// breathes, a thin gradient ring with a highlight travelling slowly around it,
// and the occasional spark. Everything here is decoration — it never takes a
// pointer event and never moves the portrait itself, which the spec keeps
// explicitly still («сам портрет не масштабировать постоянно»).
//
// Durations come straight from the spec: glow ~3 s, ring sweep ~9 s, a spark
// alive ~1.3–1.7 s and appearing irregularly. Under reduced motion nothing is
// started at all and the static end state is drawn instead.
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

import { RING_WIDTH } from './widgetPosition';
import { SPARKS } from './auraTiming';
import { ensureAuraKeyframes, type BreathVariant } from './auraWebKeyframes';

// CORE-1238 — the motion is CSS (see ./auraWebKeyframes). Every `Animated` loop
// here used to commit React once a frame from the app root, which starved the
// route Suspense retry and left /dashboard on the global loader on a slow CPU.
ensureAuraKeyframes();

/** RNW turns `dataSet` into `data-*` attributes, which the keyframes target. */
const anim = (key: string, value: string) => ({ dataSet: { [key]: value } } as any);

interface Props {
  size: number;
  /** Hover, focus or an open chat — the glow leans in. */
  active: boolean;
  /** The glow that reads as warmth on a dark page vanishes on a light one, so
   *  the light theme carries denser colour (see below). */
  isDark: boolean;
  reduceMotion: boolean;
}

export const LauncherAura: React.FC<Props> = ({ size, active, isDark, reduceMotion }) => {
  // Checked against the light theme in a browser: a wider, denser halo read as a
  // milky blob washing out the text underneath rather than as her warmth. Kept
  // tight, and most of the softness comes from the shadow's blur below.
  const glowSize = size * 1.3;
  // Reported: «на светлой теме при hover свечение почти не заметно». The alpha
  // that reads as warmth over a dark page dissolves into a near-white one, so
  // the light theme gets denser colour, a wider bloom, and a hover range that
  // leans in much further; dark keeps its original restraint.
  const glowColor = isDark ? 'rgba(233, 130, 69, 0.24)' : 'rgba(232, 103, 33, 0.45)';
  const glowShadow = isDark
    ? '0 0 22px 7px rgba(240, 150, 80, 0.38)'
    : '0 0 28px 10px rgba(232, 103, 33, 0.55)';
  const breathVariant: BreathVariant = !active ? 'idle' : isDark ? 'activeDark' : 'activeLight';
  // Under reduced motion nothing animates and the static end state is drawn.
  const glowStyle = reduceMotion ? { opacity: active ? (isDark ? 0.5 : 0.8) : 0.34 } : null;

  return (
    <>
      <View
        pointerEvents="none"
        {...(reduceMotion ? null : anim('agpAuraBreath', breathVariant))}
        style={[
          styles.glow,
          {
            width: glowSize,
            height: glowSize,
            borderRadius: glowSize / 2,
            left: (size - glowSize) / 2,
            top: (size - glowSize) / 2,
            backgroundColor: glowColor,
            boxShadow: glowShadow,
          } as any,
          glowStyle,
        ]}
      />

      {/* Base ring — always solid, so the sweeping highlight above never leaves
          a transparent gap in it. */}
      <View pointerEvents="none" style={[styles.ring, { borderRadius: size / 2 }]} />

      <View
        pointerEvents="none"
        {...(reduceMotion ? null : anim('agpAuraSweep', 'true'))}
        style={StyleSheet.absoluteFill}
      >
        <LinearGradient
          colors={['rgba(255, 214, 160, 0)', 'rgba(255, 214, 160, 0.95)', 'rgba(233, 130, 69, 0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[styles.sweep, { borderRadius: size / 2 }]}
        />
      </View>

      {!reduceMotion && SPARKS.map((spec, i) => {
        const radius = size / 2 + 3;
        const rad = (spec.angle * Math.PI) / 180;
        return (
          <View
            key={spec.angle}
            pointerEvents="none"
            {...anim('agpAuraSpark', String(i))}
            style={[
              styles.spark,
              {
                width: spec.size,
                height: spec.size,
                borderRadius: spec.size / 2,
                left: size / 2 + radius * Math.cos(rad) - spec.size / 2,
                top: size / 2 + radius * Math.sin(rad) - spec.size / 2,
              },
            ]}
          />
        );
      })}
    </>
  );
};

const styles = StyleSheet.create({
  glow: {
    // Colour and shadow are theme-dependent and applied inline; the soft halo
    // comes from `boxShadow` blur, honoured on web and on RN 0.81's new
    // architecture alike.
    position: 'absolute',
  },
  ring: {
    ...StyleSheet.absoluteFillObject,
    borderWidth: RING_WIDTH,
    // Warm gold, close to the reference: the ring is the widget's whole
    // identity in idle, and a 0.55-alpha terracotta hairline read as grey next
    // to the portrait's own golden hair.
    borderColor: 'rgba(240, 168, 96, 0.78)',
  },
  sweep: {
    // Fills the whole disc; the portrait is drawn on top inset by the ring's
    // width, so all that stays visible is the highlight running around the ring.
    flex: 1,
  },
  spark: {
    position: 'absolute',
    backgroundColor: '#FFE3BC',
    boxShadow: '0 0 6px 1px rgba(255, 214, 160, 0.9)',
  } as any,
});
